import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Chart from '../MasterAdmin/ui/Chart';
import { 
  fetchSalesExecutiveDashboardData,
  SalesExecutiveDashboardData 
} from '../../services/SalesExecutive/salesExecutiveQuickStatsService';

// Vendor role names mapping
const ROLE_NAMES = {
  1: "Hospital",
  2: "Clinic", 
  3: "Diagnostic Center",
  4: "Medical Store",
  5: "Ambulance Agency",
  6: "Blood Bank",
  8: "Product Partner"
};

// Default data structure
const defaultDashboardData: SalesExecutiveDashboardData = {
  vendorsOnboardedThisMonth: 0,
  pendingVerifications: 0,
  averageOnboardingTime: "0 days",
  totalVendorsAdded: 0,
  vendorWiseCounts: {},
  vendorStatusDistribution: {
    approved: 0,
    pending: 0,
    rejected: 0
  },
  monthlyOnboardingTrend: [],
  vendorCategoryDistribution: []
};

const SalesExecutiveDashboard: React.FC = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<SalesExecutiveDashboardData>(defaultDashboardData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        setError(null);

        const data = await fetchSalesExecutiveDashboardData(user.id);
        // Ensure all array fields are properly initialized
        setDashboardData({
          ...defaultDashboardData,
          ...data,
          monthlyOnboardingTrend: data.monthlyOnboardingTrend || [],
          vendorCategoryDistribution: data.vendorCategoryDistribution || [],
          vendorStatusDistribution: data.vendorStatusDistribution || defaultDashboardData.vendorStatusDistribution,
          vendorWiseCounts: data.vendorWiseCounts || {}
        });
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user?.id]);

  // Transform monthly trend data for chart
  const monthlyTrendChartData = (dashboardData.monthlyOnboardingTrend || []).map(item => ({
    month: item.month,
    onboarded: item.count,
    pending: 0 // Since the API doesn't provide pending count separately
  }));

  // Transform vendor status distribution for chart
  const statusDistributionChartData = [
    { status: 'Approved', count: dashboardData.vendorStatusDistribution?.approved || 0 },
    { status: 'Pending', count: dashboardData.vendorStatusDistribution?.pending || 0 },
    { status: 'Rejected', count: dashboardData.vendorStatusDistribution?.rejected || 0 }
  ];

  // Transform vendor category distribution for chart
  const categoryDistributionChartData = (dashboardData.vendorCategoryDistribution || []).map(item => ({
    category: item.label,
    count: item.value
  }));

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Shimmer */}
        <div className="flex justify-between items-center">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-64"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-32"></div>
        </div>

        {/* Quick Stats Shimmer */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-32 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded-lg p-4 animate-pulse">
                <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-16 mb-2"></div>
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-24"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Vendor-wise Counts and Charts Shimmer */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-48 mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse w-24"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse w-8"></div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-48 mb-4"></div>
            <div className="flex items-center justify-center h-48">
              <div className="w-32 h-32 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Charts Section Shimmer */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-48 mb-4"></div>
            <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-48 mb-4"></div>
            <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
            <div className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Welcome, {user?.name || 'Sales Executive'}!
        </h2>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Last updated: {new Date().toLocaleDateString()}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          🔹 Quick Stats
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
            <div className="text-2xl font-bold">{dashboardData.vendorsOnboardedThisMonth}</div>
            <div className="text-sm opacity-90">Vendors Onboarded This Month</div>
          </div>
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-4 text-white">
            <div className="text-2xl font-bold">{dashboardData.pendingVerifications}</div>
            <div className="text-sm opacity-90">Pending Verifications</div>
          </div>
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
            <div className="text-2xl font-bold">{dashboardData.averageOnboardingTime}</div>
            <div className="text-sm opacity-90">Average Onboarding Time</div>
          </div>
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
            <div className="text-2xl font-bold">{dashboardData.totalVendorsAdded}</div>
            <div className="text-sm opacity-90">Total Vendors Added</div>
          </div>
        </div>
      </div>

      {/* Vendor-wise Counts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Vendor-wise Counts (Onboarded)
          </h3>
          <div className="space-y-3">
            {Object.entries(dashboardData.vendorWiseCounts || {}).map(([category, count]) => (
              <div key={category} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-gray-700 dark:text-gray-300 font-medium">{category}</span>
                <span className="text-lg font-bold text-blue-600 dark:text-blue-400">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Vendor Status Distribution Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Vendor Status Distribution
          </h3>
          <Chart
            data={statusDistributionChartData}
            xKey="status"
            yKeys={['count']}
            type="pie"
            height={250}
            colors={['#10b981', '#f59e0b', '#ef4444']}
          />
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Onboarding Trend */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Monthly Onboarding Trend
          </h3>
          <Chart
            data={monthlyTrendChartData}
            xKey="month"
            yKeys={['onboarded']}
            type="bar"
            height={300}
            colors={['#3b82f6']}
            title=""
          />
        </div>

        {/* Vendor Category Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Vendor Category Distribution
          </h3>
          <Chart
            data={categoryDistributionChartData}
            xKey="category"
            yKeys={['count']}
            type="bar"
            height={300}
            colors={['#8b5cf6']}
            title=""
          />
        </div>
      </div>


    </div>
  );
};

export default SalesExecutiveDashboard; 